import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiX, FiUpload, FiFile, FiAlertTriangle } from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import homeworkService from "../../services/homeworkService";
import subjectService from "../../services/subjectService";
import courseService from "../../services/courseService"; // same import ExamForm.jsx uses
import employeeService from "../../services/employeeService"; // ASSUMPTION: adjust path/shape if named differently — see note below
import { getYearOptions } from "../../utils/courseYears"; // same util ExamForm.jsx uses

const EMPTY_FORM = {
    course: "",
    yearNumber: "",
    courseAdmitted: "", // populated on load, for edit-mode display only (legacy string, locked after creation)
    courseYear: "",
    subject: "",
    teacher: "",
    title: "",
    description: "",
    dueDate: "",
    maxMarks: 0,
    allowLateSubmission: false,
    isActive: true,
};

const getTeacherLabel = (teacher) => {
    if (!teacher) return "";
    if (typeof teacher === "string") return teacher;
    return teacher.fullName || teacher.name || teacher.displayName || teacher.employeeCode || "";
};

function Field({ label, required, children, error }) {
    return (
        <div className="form-group">
            <label className="form-label">
                {label}
                {required && <span style={{ color: "var(--danger)" }}> *</span>}
            </label>
            {children}
            {error && <span className="form-error">{error}</span>}
        </div>
    );
}

export default function HomeworkForm() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { academicYear } = useApp();
    const isEdit = Boolean(id);

    const [form, setForm] = useState(EMPTY_FORM);
    const [courses, setCourses] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [subjectOptions, setSubjectOptions] = useState([]); // filtered to the selected course+year
    const [subjectLoading, setSubjectLoading] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [errors, setErrors] = useState({});
    const [saving, setSaving] = useState(false);
    const [fetching, setFetching] = useState(isEdit);

    // ── Load active courses (same call ExamForm.jsx makes) ─────────────────
    useEffect(() => {
        (async () => {
            try {
                const res = await courseService.getAll({ isActive: true });
                setCourses(res.data || []);
            } catch (err) {
                toast.error("Couldn't load courses: " + err.message);
            }
        })();
    }, []);

    // ── Load teachers for the "Assigning Teacher" field ─────────────────────
    // The backend can't always auto-fill the teacher (admin accounts have no
    // linkedEmployee — only teacher-role accounts do), so this is always
    // required explicitly rather than only shown conditionally by role.
    useEffect(() => {
        (async () => {
            try {
                const res = await employeeService.getAll({
                    status: "Active",
                    employeeCategory: "Teaching",
                    limit: 9999,
                });
                const list = res?.data?.data ?? res?.data ?? [];
                setTeachers(Array.isArray(list) ? list : []);
            } catch (err) {
                toast.error("Couldn't load teachers: " + err.message);
            }
        })();
    }, []);

    const selectedCourse = courses.find((c) => c._id === form.course);
    const yearOptions = selectedCourse ? getYearOptions(selectedCourse) : [];
    const selectedYearOption = yearOptions.find((y) => y.yearNumber === Number(form.yearNumber));
    // Falls back to the legacy strings once loaded in edit mode, same reasoning
    // as ExamForm.jsx — this is what subjectService.getAll() actually queries against.
    const courseLabel = selectedCourse?.shortName || form.courseAdmitted || "";
    const yearLabel = selectedYearOption?.label || form.courseYear || "";

    // ── Fetch subjects for the selected course+year ─────────────────────────
    const fetchSubjects = useCallback(async (course, year) => {
        if (!course || !year) {
            setSubjectOptions([]);
            return;
        }
        setSubjectLoading(true);
        try {
            const res = await subjectService.getAll({
                courseAdmitted: course,
                courseYear: year,
                isActive: true,
            });
            const list = res?.data || res || [];
            setSubjectOptions(Array.isArray(list) ? list : []);
        } catch {
            setSubjectOptions([]);
        } finally {
            setSubjectLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchSubjects(courseLabel, yearLabel);
    }, [courseLabel, yearLabel, fetchSubjects]);

    // ── Load existing homework in edit mode ─────────────────────────────────
    useEffect(() => {
        if (!isEdit) return;
        setFetching(true);
        homeworkService
            .getHomeworkById(id)
            .then((res) => {
                const hw = res.data?.data ?? res.data;
                if (hw.teacher?._id) {
                    setTeachers((prev) =>
                        prev.some((t) => t._id === hw.teacher._id) ? prev : [...prev, hw.teacher],
                    );
                }
                setForm((prev) => ({
                    ...prev,
                    courseAdmitted: hw.courseAdmitted,
                    courseYear: hw.courseYear,
                    subject: hw.subject?._id || hw.subject,
                    teacher: hw.teacher?._id || hw.teacher,
                    title: hw.title,
                    description: hw.description || "",
                    dueDate: hw.dueDate ? hw.dueDate.slice(0, 10) : "",
                    maxMarks: hw.maxMarks,
                    allowLateSubmission: hw.allowLateSubmission,
                    isActive: hw.isActive,
                }));
                setAttachments(hw.attachments || []);
            })
            .catch((err) => {
                toast.error(err.response?.data?.message || err.message || "Failed to load homework");
                navigate("/homework");
            })
            .finally(() => setFetching(false));
    }, [id, isEdit, navigate]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
            ...(name === "course" ? { yearNumber: "", subject: "" } : {}),
            ...(name === "yearNumber" ? { subject: "" } : {}),
        }));
        setErrors((prev) => ({ ...prev, [name]: "" }));
    };

    const handleFileSelect = async (e) => {
        const files = Array.from(e.target.files || []);
        if (!files.length) return;

        setUploading(true);
        try {
            for (const file of files) {
                const res = await homeworkService.uploadHomeworkAttachment(file);
                setAttachments((prev) => [...prev, res.data.data]);
            }
            toast.success(`${files.length} file(s) uploaded`);
        } catch (err) {
            toast.error(err.response?.data?.message || "File upload failed");
        } finally {
            setUploading(false);
            e.target.value = "";
        }
    };

    const removeAttachment = (index) =>
        setAttachments((prev) => prev.filter((_, i) => i !== index));

    // ── Validation ────────────────────────────────────────────────────────
    const validate = () => {
        const errs = {};
        if (!form.title.trim()) errs.title = "Title is required.";
        if (!form.dueDate) errs.dueDate = "Due date is required.";
        if (!academicYear?._id) errs._global = "Select an academic year from the Dashboard first.";

        if (!isEdit) {
            if (!form.course) errs.course = "Course is required.";
            if (!form.yearNumber) errs.yearNumber = "Course year is required.";
            if (!form.subject) errs.subject = "Subject is required.";
            if (!form.teacher) errs.teacher = "Assigning teacher is required.";
        }

        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    // ── Submit ────────────────────────────────────────────────────────────
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSaving(true);
        try {
            if (isEdit) {
                await homeworkService.updateHomework(id, {
                    title: form.title,
                    description: form.description,
                    dueDate: form.dueDate,
                    maxMarks: form.maxMarks,
                    allowLateSubmission: form.allowLateSubmission,
                    isActive: form.isActive,
                    attachments,
                });
                toast.success("Homework updated successfully");
            } else {
                await homeworkService.createHomework({
                    subject: form.subject,
                    teacher: form.teacher,
                    academicYear: academicYear._id,
                    courseAdmitted: courseLabel,
                    courseYear: yearLabel,
                    title: form.title,
                    description: form.description,
                    dueDate: form.dueDate,
                    maxMarks: form.maxMarks,
                    allowLateSubmission: form.allowLateSubmission,
                    attachments,
                });
                toast.success("Homework assigned successfully");
            }
            navigate("/homework");
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to save homework");
        } finally {
            setSaving(false);
        }
    };

    if (fetching) return <div className="loading-center"><div className="spinner" />Loading homework…</div>;

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.5rem" }}>
                <div>
                    <h1 className="page-title">{isEdit ? "Edit Homework" : "Assign Homework"}</h1>
                    <p className="page-subtitle">{academicYear?.label || "No academic year selected"}</p>
                </div>
                <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/homework")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="button" className="btn btn-primary" onClick={handleSubmit} disabled={saving || uploading}>
                        <FiSave size={14} /> {saving ? "Saving…" : isEdit ? "Update Homework" : "Assign Homework"}
                    </button>
                </div>
            </div>

            {errors._global && (
                <div style={{ background: "#fef3c7", border: "1px solid #fcd34d", borderRadius: 8, padding: "0.75rem 1rem", marginBottom: "1rem", fontSize: 13, color: "#92400e", display: "flex", alignItems: "center", gap: 8 }}>
                    <FiAlertTriangle size={14} /> {errors._global}
                </div>
            )}

            <form onSubmit={handleSubmit}>
                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-header">Homework Details</div>
                    <div className="card-body">
                        <div className="form-grid">
                            <Field label="Course" required error={errors.course}>
                                {isEdit ? (
                                    <>
                                        <input className="form-control" value={form.courseAdmitted} disabled />
                                        <span className="form-hint">Course can't be changed after creation.</span>
                                    </>
                                ) : (
                                    <select
                                        name="course"
                                        value={form.course}
                                        onChange={handleChange}
                                        className={`form-control ${errors.course ? "error" : ""}`}
                                    >
                                        <option value="">Select course…</option>
                                        {courses.map((c) => (
                                            <option key={c._id} value={c._id}>
                                                {c.shortName}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </Field>

                            <Field label="Course Year" required error={errors.yearNumber}>
                                {isEdit ? (
                                    <input className="form-control" value={form.courseYear} disabled />
                                ) : (
                                    <select
                                        name="yearNumber"
                                        value={form.yearNumber}
                                        onChange={handleChange}
                                        className={`form-control ${errors.yearNumber ? "error" : ""}`}
                                        disabled={!form.course}
                                    >
                                        <option value="">{form.course ? "Select year…" : "Select a course first"}</option>
                                        {yearOptions.map((y) => (
                                            <option key={y.yearNumber} value={y.yearNumber}>
                                                {y.label}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </Field>

                            <Field label="Subject" required error={errors.subject}>
                                {isEdit ? (
                                    <input
                                        className="form-control"
                                        value={subjectOptions.find((s) => s._id === form.subject)?.name || "Loading…"}
                                        disabled
                                    />
                                ) : (
                                    <select
                                        name="subject"
                                        value={form.subject}
                                        onChange={handleChange}
                                        className={`form-control ${errors.subject ? "error" : ""}`}
                                        disabled={!courseLabel || !yearLabel}
                                    >
                                        <option value="">
                                            {subjectLoading
                                                ? "Loading subjects…"
                                                : courseLabel && yearLabel
                                                    ? "Select subject…"
                                                    : "Select course & year first"}
                                        </option>
                                        {subjectOptions.map((s) => (
                                            <option key={s._id} value={s._id}>
                                                {s.name}
                                                {s.code ? ` (${s.code})` : ""}
                                            </option>
                                        ))}
                                    </select>
                                )}
                                {!subjectLoading && courseLabel && yearLabel && subjectOptions.length === 0 && !isEdit && (
                                    <span className="form-hint" style={{ color: "#92400e" }}>
                                        No subjects found for {courseLabel} — {yearLabel}.{" "}
                                        <a href="/subjects/new" target="_blank" rel="noreferrer">Add subjects →</a>
                                    </span>
                                )}
                            </Field>

                            <Field label="Assigning Teacher" required error={errors.teacher}>
                                {isEdit ? (
                                    <input
                                        className="form-control"
                                        value={getTeacherLabel(teachers.find((t) => t._id === form.teacher)) || "Loading…"}
                                        disabled
                                    />
                                ) : (
                                    <select
                                        name="teacher"
                                        value={form.teacher}
                                        onChange={handleChange}
                                        className={`form-control ${errors.teacher ? "error" : ""}`}
                                    >
                                        <option value="">Select teacher…</option>
                                        {teachers.map((t) => (
                                            <option key={t._id} value={t._id}>
                                                {getTeacherLabel(t)}
                                            </option>
                                        ))}
                                    </select>
                                )}
                            </Field>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">
                                    Title <span style={{ color: "var(--danger)" }}>*</span>
                                </label>
                                <input
                                    name="title"
                                    value={form.title}
                                    onChange={handleChange}
                                    placeholder="e.g. Chapter 3 Exercises"
                                    className={`form-control ${errors.title ? "error" : ""}`}
                                    autoFocus
                                />
                                {errors.title && <span className="form-error">{errors.title}</span>}
                            </div>

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label">Description / Instructions</label>
                                <textarea
                                    name="description"
                                    value={form.description}
                                    onChange={handleChange}
                                    rows={4}
                                    placeholder="Optional instructions for students"
                                    className="form-control"
                                    style={{ resize: "vertical" }}
                                />
                            </div>

                            <Field label="Due Date" required error={errors.dueDate}>
                                <input
                                    type="date"
                                    name="dueDate"
                                    value={form.dueDate}
                                    onChange={handleChange}
                                    className={`form-control ${errors.dueDate ? "error" : ""}`}
                                />
                            </Field>

                            <Field label="Max Marks">
                                <input
                                    type="number"
                                    name="maxMarks"
                                    min={0}
                                    value={form.maxMarks}
                                    onChange={handleChange}
                                    placeholder="0 = not numerically graded"
                                    className="form-control"
                                />
                            </Field>

                            {isEdit && (
                                <Field label="Status">
                                    <select
                                        name="isActive"
                                        value={form.isActive ? "active" : "inactive"}
                                        onChange={(e) =>
                                            setForm((prev) => ({ ...prev, isActive: e.target.value === "active" }))
                                        }
                                        className="form-control"
                                    >
                                        <option value="active">Active</option>
                                        <option value="inactive">Inactive</option>
                                    </select>
                                </Field>
                            )}

                            <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                                <label className="form-label" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <input
                                        type="checkbox"
                                        name="allowLateSubmission"
                                        checked={form.allowLateSubmission}
                                        onChange={handleChange}
                                    />
                                    Allow late submission (flagged as "Late" instead of being blocked)
                                </label>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card" style={{ marginBottom: "1rem" }}>
                    <div className="card-header">Attachments (question paper, reference material)</div>
                    <div className="card-body">
                        <label className="btn btn-outline" style={{ width: "fit-content", cursor: "pointer" }}>
                            <FiUpload size={13} /> {uploading ? "Uploading…" : "Choose Files"}
                            <input type="file" multiple hidden onChange={handleFileSelect} disabled={uploading} />
                        </label>

                        {attachments.length > 0 && (
                            <div style={{ marginTop: 10 }}>
                                {attachments.map((a, i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, marginBottom: 4 }}>
                                        <FiFile size={13} />
                                        <a href={a.url} target="_blank" rel="noreferrer">{a.fileName}</a>
                                        <button
                                            type="button"
                                            onClick={() => removeAttachment(i)}
                                            style={{ background: "transparent", border: "none", color: "#dc2626", cursor: "pointer" }}
                                        >
                                            <FiX size={13} />
                                        </button>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                    <button type="button" className="btn btn-outline" onClick={() => navigate("/homework")}>
                        <FiX size={14} /> Cancel
                    </button>
                    <button type="submit" className="btn btn-primary" disabled={saving || uploading}>
                        <FiSave size={14} /> {saving ? "Saving…" : isEdit ? "Update Homework" : "Assign Homework"}
                    </button>
                </div>
            </form>
        </div>
    );
}
